// engine-exception: the public status page is what a customer opens during an outage — no account, no shell, no engine client auth; a slug capability page like the public quote
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ApiError, type Client } from '../../engine/api/client';
import { publicClient } from '../booking/BookingPage';

// The page a client looks at instead of phoning (docs/servicedesk-v2-proposal.md P9). It reads
// /public/status/:slug, which needs no auth — the slug is the capability. Deliberately plain: it
// is opened on a phone by someone who is already annoyed, and it has to load when everything else
// is on fire, so there is no shell, no sidebar and no engine client behind it.

interface Update { at: string; body: string; kind: string }
interface Incident { title: string; severity: string; state: string; started_at: string; resolved_at: string | null; updates: Update[] }
interface Maintenance { title: string; description: string | null; starts_at: string; ends_at: string; status: string }
interface Status {
  title: string; intro: string | null; support_email: string | null; support_phone: string | null;
  status: 'operational' | 'degraded' | 'outage' | 'maintenance' | string;
  incidents: Incident[]; maintenance: Maintenance[]; generated_at: string;
}

const HEADLINE: Record<string, { label: string; colour: string }> = {
  operational: { label: 'All systems normal', colour: '#177245' },
  degraded: { label: 'We are working on an issue', colour: '#a8620a' },
  outage: { label: 'There is a major outage', colour: '#a81c1c' },
  maintenance: { label: 'Planned work is under way', colour: '#3a5ea8' },
};
const STATE_LABEL: Record<string, string> = { declared: 'Live', mitigated: 'Under control', resolved: 'Resolved', closed: 'Closed' };

const when = (iso: string) => new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export function StatusPage({ client }: { client?: Client } = {}) {
  const { slug = '' } = useParams();
  const api = useMemo(() => client ?? publicClient(), [client]);
  const [state, setState] = useState<{ loading: boolean; error: string | null; data: Status | null }>({ loading: true, error: null, data: null });

  useEffect(() => {
    let live = true;
    const load = async () => {
      try {
        const data = await api.request<Status>(`/public/status/${encodeURIComponent(slug)}`);
        if (live) setState({ loading: false, error: null, data });
      } catch (e) {
        const msg = e instanceof ApiError ? (e.detail ?? e.message) : e instanceof Error ? e.message : 'This page is not available';
        if (live) setState({ loading: false, error: msg, data: null });
      }
    };
    void load();
    // People leave this page open during an outage; refresh it for them.
    const timer = setInterval(() => { void load(); }, 60_000);
    return () => { live = false; clearInterval(timer); };
  }, [api, slug]);

  if (state.loading) return <main style={wrap}><p>Loading…</p></main>;
  if (state.error || !state.data) return <main style={wrap}><h1 style={{ fontSize: 20 }}>Nothing here</h1><p>{state.error ?? 'This page is not available.'}</p></main>;

  const s = state.data;
  const head = HEADLINE[s.status] ?? HEADLINE['operational']!;
  const live = s.incidents.filter((i) => i.state === 'declared' || i.state === 'mitigated');
  const past = s.incidents.filter((i) => i.state === 'resolved' || i.state === 'closed');

  return (
    <main style={wrap}>
      <h1 style={{ fontSize: 24, margin: '0 0 4px' }}>{s.title}</h1>
      {s.intro && <p style={{ color: '#5c574f', marginTop: 0 }}>{s.intro}</p>}

      <div role="status" style={{ ...banner, background: head.colour }}>{head.label}</div>

      {live.length > 0 && (
        <section aria-label="Happening now">
          <h2 style={h2}>Happening now</h2>
          {live.map((i) => <IncidentCard key={`${i.title}-${i.started_at}`} incident={i} />)}
        </section>
      )}

      {s.maintenance.length > 0 && (
        <section aria-label="Planned work">
          <h2 style={h2}>Planned work</h2>
          {s.maintenance.map((m) => (
            <article key={`${m.title}-${m.starts_at}`} style={card}>
              <strong>{m.title}</strong>
              <div style={meta}>{when(m.starts_at)} — {when(m.ends_at)}</div>
              {m.description && <p style={{ margin: '8px 0 0' }}>{m.description}</p>}
            </article>
          ))}
        </section>
      )}

      {past.length > 0 && (
        <section aria-label="Recently">
          <h2 style={h2}>Recently</h2>
          {past.map((i) => <IncidentCard key={`${i.title}-${i.started_at}`} incident={i} />)}
        </section>
      )}

      {live.length === 0 && past.length === 0 && s.maintenance.length === 0 && (
        <p style={{ color: '#5c574f' }}>Nothing to report.</p>
      )}

      {(s.support_email || s.support_phone) && (
        <footer style={{ marginTop: 32, fontSize: 13, color: '#5c574f' }}>
          Need help? {s.support_email && <a href={`mailto:${s.support_email}`}>{s.support_email}</a>}
          {s.support_email && s.support_phone && ' · '}
          {s.support_phone && <a href={`tel:${s.support_phone}`}>{s.support_phone}</a>}
        </footer>
      )}
      <p style={{ fontSize: 12, color: '#8a8377' }}>Updated {when(s.generated_at)}</p>
    </main>
  );
}

function IncidentCard({ incident }: { incident: Incident }) {
  return (
    <article style={card}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>{incident.title}</strong>
        <span style={pill}>{STATE_LABEL[incident.state] ?? incident.state}</span>
      </div>
      <div style={meta}>Started {when(incident.started_at)}{incident.resolved_at ? ` · resolved ${when(incident.resolved_at)}` : ''}</div>
      {/* Newest first: during an outage the latest line is the only one anyone reads. */}
      <ol style={{ listStyle: 'none', padding: 0, margin: '10px 0 0' }}>
        {[...incident.updates].reverse().map((u, i) => (
          <li key={`${u.at}-${i}`} style={{ borderLeft: '3px solid #d7d2c7', padding: '2px 0 2px 10px', marginBottom: 8 }}>
            <div style={meta}>{when(u.at)}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{u.body}</div>
          </li>
        ))}
      </ol>
    </article>
  );
}

const wrap: React.CSSProperties = { maxWidth: 720, margin: '0 auto', padding: '32px 20px 64px', fontFamily: 'system-ui, sans-serif', color: '#1b1915', lineHeight: 1.5 };
const banner: React.CSSProperties = { color: '#fff', fontWeight: 700, padding: '14px 16px', borderRadius: 10, margin: '16px 0 24px' };
const h2: React.CSSProperties = { fontSize: 15, textTransform: 'uppercase', letterSpacing: '0.06em', color: '#5c574f', margin: '24px 0 8px' };
const card: React.CSSProperties = { border: '1px solid #e4e0d8', borderRadius: 10, padding: 14, marginBottom: 12, background: '#fff' };
const meta: React.CSSProperties = { fontSize: 12, color: '#8a8377' };
const pill: React.CSSProperties = { fontSize: 12, border: '1px solid #d7d2c7', borderRadius: 999, padding: '1px 8px', color: '#5c574f' };
